import React, { useEffect, useState } from 'react';
import { Box, Checkbox, Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSelector } from 'react-redux';
import { useToast } from 'use-toast-mui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import CommonModalButton from '../../../../form-components/CommonModalButton';
import CommonLoading from '../../../../form-components/CommonLoading';
import { updateGroupLessonTemplate, getTemplateLinkedClasses } from '../../../../api/lesson';

import {
  EditContainer,
  ModalHeader,
  ModalHeaderTitle,
  EditContent,
  TemplateSummaryCard,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  AppliedLessonsHeader,
  AppliedLessonsTitle,
  AppliedLessonsNote,
  AppliedLessonsTable,
  ModalFooter,
} from './GroupLessonTemplateStyled';

const LinkedScheduleModal = ({ open, onClose, template }) => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const user = useSelector(state => state.user.user);

  const [selectedRowIndices, setSelectedRowIndices] = useState([]);

  const { data: linkedClasses = [], isLoading } = useQuery({
    queryKey: ['templateLinkedClasses', template?.id],
    queryFn: () => getTemplateLinkedClasses(template.id, user.token),
    enabled: !!template?.id && !!user?.token && open,
  });

  useEffect(() => {
    if (open) {
      setSelectedRowIndices([]);
    }
  }, [open]);

  const handleToggleRow = index => {
    setSelectedRowIndices(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index],
    );
  };

  const handleToggleAll = () => {
    if (selectedRowIndices.length === linkedClasses.length) {
      setSelectedRowIndices([]);
    } else {
      setSelectedRowIndices(linkedClasses.map((_, i) => i));
    }
  };

  const selectedClassTimeIds = selectedRowIndices
    .map(i => linkedClasses[i]?.courseClassTimeId)
    .filter(Boolean);

  const applyMutation = useMutation({
    mutationFn: ({ id, data }) => updateGroupLessonTemplate(id, data, user.token),
    onSuccess: () => {
      toast.success('선택한 스케줄에 그룹수업 설정이 적용되었습니다');
      queryClient.invalidateQueries({ queryKey: ['groupLessonTemplates'] });
      queryClient.invalidateQueries({ queryKey: ['groupLessons'] });
      queryClient.invalidateQueries({ queryKey: ['templateLinkedClasses'] });
      onClose();
    },
  });

  const handleApply = () => {
    if (selectedClassTimeIds.length === 0) {
      toast.error('적용할 스케줄을 선택해주세요');
      return;
    }

    applyMutation.mutate({
      id: template.id,
      data: {
        templateName: template.templateName,
        classDuration: template.classDuration,
        ticketDeductionCount: template.ticketDeductionCount,
        membersMaxCount: template.membersMaxCount,
        membersWaitMaxCount: template.membersWaitMaxCount,
        ticketIds: template.ticketIds,
        applyClassTimeIds: selectedClassTimeIds,
      },
    });
  };

  const allSelected = linkedClasses.length > 0 && selectedRowIndices.length === linkedClasses.length;

  return (
    <Modal open={open} onClose={onClose} sx={{ '& :focus-visible': { outline: 'none' } }}>
      <EditContainer>
        <ModalHeader>
          <ModalHeaderTitle>연결된 스케줄</ModalHeaderTitle>
          <CloseIcon onClick={onClose} sx={{ color: '#fff', cursor: 'pointer', '&:hover': { opacity: 0.7 } }} />
        </ModalHeader>

        <EditContent>
          {/* 그룹수업 설정 요약 */}
          <TemplateSummaryCard>
            <SummaryItem sx={{ flex: 1 }}>
              <SummaryLabel>그룹수업명</SummaryLabel>
              <SummaryValue>{template?.templateName}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>수업 시간</SummaryLabel>
              <SummaryValue>{template?.classDuration}분</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>정원</SummaryLabel>
              <SummaryValue>{template?.membersMaxCount}명</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>대기</SummaryLabel>
              <SummaryValue>{template?.membersWaitMaxCount}명</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>차감</SummaryLabel>
              <SummaryValue>{template?.ticketDeductionCount}회</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>수강권</SummaryLabel>
              <SummaryValue>{template?.selectedTicketCount}개</SummaryValue>
            </SummaryItem>
          </TemplateSummaryCard>

          {/* 적용될 수업 테이블 */}
          <AppliedLessonsHeader>
            <AppliedLessonsTitle>적용될 수업 ({linkedClasses.length}건)</AppliedLessonsTitle>
            <AppliedLessonsNote>
              *선택한 수업에 위 그룹수업 설정이 적용됩니다.
            </AppliedLessonsNote>
          </AppliedLessonsHeader>

          <AppliedLessonsTable>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>
                  <Checkbox
                    size="small"
                    checked={allSelected}
                    indeterminate={selectedRowIndices.length > 0 && !allSelected}
                    onChange={handleToggleAll}
                    sx={{ p: 0, color: '#999', '&.Mui-checked': { color: '#4AB3BC' }, '&.MuiCheckbox-indeterminate': { color: '#4AB3BC' } }}
                  />
                </th>
                <th>수업일</th>
                <th>수업 시간</th>
                <th>수업명</th>
                <th>강사</th>
                <th>강의실</th>
                <th>예약 인원</th>
                <th>대기 인원</th>
                <th>수강권</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={9}>
                    <CommonLoading label="스케줄 불러오는 중..." minHeight={120} />
                  </td>
                </tr>
              ) : linkedClasses.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ padding: '40px', color: '#999' }}>
                    연결된 스케줄이 없습니다
                  </td>
                </tr>
              ) : (
                linkedClasses.map((cls, index) => (
                  <tr key={cls.courseClassTimeId || index}>
                    <td>
                      <Checkbox
                        size="small"
                        checked={selectedRowIndices.includes(index)}
                        onChange={() => handleToggleRow(index)}
                        sx={{ p: 0, color: '#999', '&.Mui-checked': { color: '#4AB3BC' } }}
                      />
                    </td>
                    <td>{cls.nearestClassDate}</td>
                    <td>{cls.nearestStartTime}~{cls.nearestEndTime}</td>
                    <td>{cls.className}</td>
                    <td>{cls.trainerName}</td>
                    <td>{cls.exerciseRoomName}</td>
                    <td>{cls.currentReservedCount}명</td>
                    <td>{cls.currentWaitCount}명</td>
                    <td>{cls.selectedTicketCount}개</td>
                  </tr>
                ))
              )}
            </tbody>
          </AppliedLessonsTable>
        </EditContent>

        <ModalFooter>
          <CommonModalButton variant="cancel" onClick={onClose}>
            닫기
          </CommonModalButton>
          <CommonModalButton variant="submit" onClick={handleApply} disabled={applyMutation.isPending}>
            {applyMutation.isPending ? '적용 중...' : `선택 스케줄에 적용 (${selectedClassTimeIds.length}건)`}
          </CommonModalButton>
        </ModalFooter>
      </EditContainer>
    </Modal>
  );
};

export default LinkedScheduleModal;
